import { invalidatePolicyViews } from "../../../../lib/policyQueries";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { Alert } from "../../../../components/ui/Alert";
import { Badge } from "../../../../components/ui/Badge";
import { Button } from "../../../../components/ui/Button";
import { Card } from "../../../../components/ui/Card";
import { ConfirmDialog } from "../../../../components/ui/ConfirmDialog";
import { DevicePicker } from "../../../../components/devices/DevicePicker";
import { FormField } from "../../../../components/ui/FormField";
import { Input } from "../../../../components/ui/Input";
import { Modal } from "../../../../components/ui/Modal";
import { Select } from "../../../../components/ui/Select";
import { useToast } from "../../../../components/ui/Toast";
import { UserSelect } from "../../../../components/ui/UserSelect";
import { ApiError } from "../../../../lib/api";
import { type DeviceListRow } from "../../../../lib/api/devices";
import { deviceGroupsApi } from "../../../../lib/api/network/deviceGroups";
import {
  type WorkScheduleAssignment,
  type WorkScheduleAssignmentTargetType,
  schedulesApi,
} from "../../../../lib/api/policies";
import { t } from "../../../../lib/i18n";

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t(`errors.${err.code}`, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

interface ScheduleAssignmentsTabProps {
  scheduleId: string;
  assignments: WorkScheduleAssignment[];
}

export function ScheduleAssignmentsTab({
  scheduleId,
  assignments,
}: ScheduleAssignmentsTabProps) {
  const qc = useQueryClient();
  const { push: pushToast } = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [targetType, setTargetType] = useState<WorkScheduleAssignmentTargetType>("device");
  const [targetId, setTargetId] = useState("");
  const [targetLabel, setTargetLabel] = useState("");
  const [priority, setPriority] = useState("100");
  const [isActive, setIsActive] = useState(true);
  const [toDelete, setToDelete] = useState<WorkScheduleAssignment | null>(null);

  const groups = useQuery({
    queryKey: ["network", "device-groups"],
    queryFn: deviceGroupsApi.list,
    enabled: createOpen && targetType === "device_group",
  });

  const resetDraft = () => {
    setTargetType("device");
    setTargetId("");
    setTargetLabel("");
    setPriority("100");
    setIsActive(true);
  };

  const create = useMutation({
    mutationFn: () =>
      schedulesApi.createAssignment(scheduleId, {
        target_type: targetType,
        target_id: targetId,
        priority: Number(priority),
        is_active: isActive,
      }),
    onSuccess: () => {
      setCreateOpen(false);
      resetDraft();
      void invalidatePolicyViews(qc);
    },
  });

  const patch = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      schedulesApi.patchAssignment(scheduleId, id, { is_active }),
    onSuccess: () => {
      void invalidatePolicyViews(qc);
    },
    onError: (err) =>
      pushToast({ tone: "danger", title: t("common.error"), body: translateError(err) }),
  });

  const remove = useMutation({
    mutationFn: (assignmentId: string) =>
      schedulesApi.deleteAssignment(scheduleId, assignmentId),
    onSuccess: () => {
      setToDelete(null);
      void invalidatePolicyViews(qc);
    },
    onError: (err) =>
      pushToast({ tone: "danger", title: t("common.error"), body: translateError(err) }),
  });

  const errMsg = create.error ? translateError(create.error) : null;
  const canSubmit = targetId.trim().length > 0 && !Number.isNaN(Number(priority));

  const onDevicePick = (device: DeviceListRow) => {
    setTargetId(device.id);
    setTargetLabel(device.model || device.device_uid);
  };

  return (
    <Card
      title={t("schedules.assignments.cardTitle")}
      actions={
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          {t("schedules.assignments.add")}
        </Button>
      }
    >
      <table className="table">
        <thead>
          <tr>
            <th>{t("schedules.assignments.column.targetType")}</th>
            <th>{t("schedules.assignments.column.target")}</th>
            <th>{t("schedules.assignments.column.priority")}</th>
            <th>{t("schedules.assignments.column.status")}</th>
            <th style={{ textAlign: "right" }}></th>
          </tr>
        </thead>
        <tbody>
          {assignments.map((row) => (
            <tr key={row.id}>
              <td>{t(`schedules.assignments.targetType.${row.target_type}`)}</td>
              <td>{row.target_display}</td>
              <td>{row.priority}</td>
              <td>
                {row.is_active ? (
                  <Badge tone="success">{t("common.active")}</Badge>
                ) : (
                  <Badge tone="neutral">{t("common.inactive")}</Badge>
                )}
              </td>
              <td style={{ textAlign: "right" }}>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      patch.mutate({ id: row.id, is_active: !row.is_active })
                    }
                    loading={patch.isPending}
                  >
                    {row.is_active
                      ? t("schedules.assignments.deactivate")
                      : t("schedules.assignments.activate")}
                  </Button>
                  <Button variant="danger" size="sm" onClick={() => setToDelete(row)}>
                    {t("common.delete")}
                  </Button>
                </div>
              </td>
            </tr>
          ))}
          {assignments.length === 0 && (
            <tr>
              <td colSpan={5} style={{ color: "var(--color-text-muted)" }}>
                {t("schedules.assignments.empty")}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <Modal
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          resetDraft();
        }}
        title={t("schedules.assignments.createTitle")}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setCreateOpen(false);
                resetDraft();
              }}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={() => create.mutate()}
              loading={create.isPending}
              disabled={!canSubmit}
            >
              {t("schedules.assignments.submitCreate")}
            </Button>
          </>
        }
      >
        {errMsg && (
          <div style={{ marginBottom: 12 }}>
            <Alert tone="danger">{errMsg}</Alert>
          </div>
        )}
        <div style={{ display: "grid", gap: 16 }}>
          <FormField label={t("schedules.assignments.field.targetType")} required>
            {(fid) => (
              <Select
                id={fid}
                value={targetType}
                onChange={(e) => {
                  setTargetType(e.target.value as WorkScheduleAssignmentTargetType);
                  setTargetId("");
                  setTargetLabel("");
                }}
              >
                <option value="device">{t("schedules.assignments.targetType.device")}</option>
                <option value="user">{t("schedules.assignments.targetType.user")}</option>
                <option value="device_group">
                  {t("schedules.assignments.targetType.device_group")}
                </option>
              </Select>
            )}
          </FormField>

          {targetType === "device" && (
            <FormField label={t("schedules.assignments.field.device")} required>
              {() => (
                <div style={{ display: "grid", gap: 8 }}>
                  <DevicePicker onSelect={onDevicePick} />
                  {targetLabel && (
                    <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
                      {t("schedules.assignments.selectedTarget")}: {targetLabel}
                    </p>
                  )}
                </div>
              )}
            </FormField>
          )}

          {targetType === "user" && (
            <FormField label={t("schedules.assignments.field.user")} required>
              {(fid) => (
                <UserSelect
                  id={fid}
                  value={targetId}
                  onChange={(id, opt) => {
                    setTargetId(id);
                    setTargetLabel(opt?.label ?? "");
                  }}
                  required
                />
              )}
            </FormField>
          )}

          {targetType === "device_group" && (
            <FormField label={t("schedules.assignments.field.deviceGroup")} required>
              {(fid) => (
                <Select
                  id={fid}
                  value={targetId}
                  onChange={(e) => {
                    const id = e.target.value;
                    setTargetId(id);
                    const group = (groups.data ?? []).find((g) => g.id === id);
                    setTargetLabel(group?.name ?? "");
                  }}
                  required
                >
                  <option value="">{t("common.select")}</option>
                  {(groups.data ?? []).map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
          )}

          <FormField label={t("schedules.assignments.field.priority")}>
            {(fid) => (
              <Input
                id={fid}
                type="number"
                min={0}
                max={10000}
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              />
            )}
          </FormField>

          <FormField label={t("common.status")}>
            {(fid) => (
              <Select
                id={fid}
                value={isActive ? "true" : "false"}
                onChange={(e) => setIsActive(e.target.value === "true")}
              >
                <option value="true">{t("common.active")}</option>
                <option value="false">{t("common.inactive")}</option>
              </Select>
            )}
          </FormField>
        </div>
      </Modal>

      <ConfirmDialog
        open={toDelete !== null}
        title={t("schedules.assignments.deleteTitle")}
        body={t("schedules.assignments.deleteBody")}
        loading={remove.isPending}
        onCancel={() => setToDelete(null)}
        onConfirm={() => toDelete && remove.mutate(toDelete.id)}
      />
    </Card>
  );
}
